import InteractivePrototype from './interactivePrototype.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import * as Notation from '../notation.ts';
import utils from '../utils.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import KeySigPlayer from './keySigPlayer.ts';
import { toHTML } from '../uTheory.Font/font.ts';

class RelativeAndParallelKeys extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeedFromSkills = RelativeAndParallelKeys.registry.skills;
    this.keySigPlayer = new KeySigPlayer();
    this.settings = {
      instructions: {
        ariaLive: 'assertive',
        ariaLiveRegion: 'all',
      },
      editor: false,
      notation: false,
      piano: false,
      letters: {
        letterButtons: true,
        customButtons: [
          { tagName: 'br' },
          { text: '#', hotKey: '#', value: '#', class: 'accidentals' },
          { text: 'n', hotKey: 'n', value: '', class: 'accidentals' },
          { text: 'b', hotKey: 'b', value: 'b', class: 'accidentals' },
        ],
        checkButton: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };

    this.answer = {
      letter: '',
      accidental: '',
    };

    this.layout = {
      instructions: { noWrap: true },
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    options.maxQuestions = options.maxQuestions || 8;
    this.skillCalculator = new SkillCalculator();

    // now call the prototype's init function:
    super.init(displayDiv, options);
  }

  generateQuestions() {
    const { options } = this;

    this.questions = this.getQuestions(options);
    this.maxQuestions = this.questions.length;

    this.questionPrompt = 'What is <strong>{question}</strong>?';
    if (!this.optionsTrue('testMode')) {
      this.questionPrompt += `<br /><small>Question #{question} of ${this.questions.length}`;
    }
  }

  getQuestions(options) {
    const drill = options.drill !== undefined && options.drill === true;
    return drill === true
      ? this.generateDrillQuestions(options)
      : this.generateManualQuestions(options);
  }

  generateManualQuestions(options) {
    return this.processQuestions(options.questions);
  }

  generateDrillQuestions(options) {
    const generator = new QuestionGenerator();
    const { maxQuestions } = options;
    const { skills } = options;

    const generatedQuestions = generator.generate(skills, maxQuestions);

    return this.processQuestions(generatedQuestions);
  }

  processQuestions(questions) {
    return questions.map((question, index) => {
      const questionParts = question.split('/');
      let parallelOrRelative = questionParts[2];
      const keyNote = questionParts[0];
      const keyQuality = questionParts[1];
      const keySig = new Notation.KeySignature(keyNote, keyQuality);
      let skills = [];

      if (parallelOrRelative === 'parallel') {
        parallelOrRelative = {
          text: 'parallel',
          keySig: this.getParallelKey(keySig),
        };
      } else {
        // Assume relative key -- since all keys have relative:
        parallelOrRelative = {
          text: 'relative',
          keySig: this.getRelativeKey(keySig),
        };
      }

      skills = this.calculateSkill(parallelOrRelative.text);
      const questionNumber = index + 1;
      const questionObj = {
        question: {
          questionText: keySig.toAccidentalFontString(),
          keySig,
          keyNote,
          keyQuality: keySig.quality,
          parallelOrRelative,
        },
        answer:
          parallelOrRelative.keySig.keyNote.letter + parallelOrRelative.keySig.keyNote.accidental,
        answerQuality: keySig.quality === 'Major' ? 'minor' : 'Major',
        text: index + 1,
        skills,
      };
      questionObj.text = [this.getQuestionText(questionObj), questionNumber];
      return questionObj;
    }, this);
  }

  getParallelOrRelative(keySig, parallelOrRelative) {
    parallelOrRelative = parallelOrRelative || utils.shuffle(['parallel', 'relative'])[0];

    if (parallelOrRelative === 'parallel') {
      return {
        keySig: this.getParallelKey(keySig),
        text: 'parallel',
      };
    }
    return {
      keySig: this.getRelativeKey(keySig),
      text: 'relative',
    };
  }

  getParallelKey(keySig) {
    return keySig.getParallelKey();
  }

  getRelativeKey(keySig) {
    const keyQuality = keySig.quality;

    if (keyQuality === 'Major') {
      return keySig.getRelativeMinor();
    }
    return keySig.getRelativeMajor();
  }

  createCustomDivs() {
    this.createAnswerDiv('div', 'answerDiv', 1);
    this.answerDiv.setAttribute('aria-live', 'polite');
  }

  createAnswerDiv(element, elementId, index) {
    this[elementId] = document.createElement(element);

    const div = this[elementId];
    const styleOptions = {
      fontSize: '4vw',
      fontWeight: 'bold',
      textAlign: 'center',
      padding: '10px',
    };

    div.id = elementId;
    div.className = '';
    div.innerHTML = '__';

    utils.merge(div.style, styleOptions);

    this.displayDiv.insertBefore(div, this.displayDiv.children[index]);
  }

  newQuestion() {
    this.progressTracker.start(this.targetSpeed);

    super.newQuestion();
    this.clearAnswer();
  }

  getQuestionText(questionObj) {
    const { question } = questionObj;

    return `${question.keySig.toAccidentalFontString()}'s ${question.parallelOrRelative.text} ${
      questionObj.answerQuality
    }`;
  }

  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.questionPrompt = 'What is <strong>{question}</strong>?';
      this.questions = this.processQuestions([questionString]);
      this.questionNumber = 0;
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners() {
    this.letterPanel.addLetterCallback(this.addLetterToAnswer, this);
    this.letterPanel.addCustomButtonCallback(this.addAccidentalToAnswer, this);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  addLetterToAnswer(letter) {
    this.answer.letter = letter;
    this.displayAnswer();
  }

  addAccidentalToAnswer(accidental) {
    this.answer.accidental = accidental;
    this.displayAnswer();
  }

  displayAnswer() {
    if (!this.answer.letter && !this.answer.accidental) {
      this.answerDiv.removeAttribute('aria-live');
      this.answerDiv.innerHTML = `__ ${this.currentQuestion.answerQuality}`;
      return;
    }

    this.answerDiv.setAttribute('aria-live', 'polite');

    const accidental = toHTML('accidental', this.answer.accidental || '');
    this.answerDiv.innerHTML = `${this.answer.letter}${accidental} ${this.currentQuestion.answerQuality}`;
  }

  clearAnswer() {
    this.answer.letter = '';
    this.answer.accidental = '';
    this.displayAnswer();
  }

  calculateSkill(parallelOrRelative) {
    return this.skillCalculator.getParallelOrRelativeKeysSkill(parallelOrRelative);
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return;
    const testMode = this.optionsTrue('testMode');
    const rightAnswer = this.questions[this.questionNumber].answer;
    const userAnswer = this.answer.letter + this.answer.accidental;
    const correct = rightAnswer === userAnswer;

    this.answerAttempts++;

    if (testMode === true) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      this.getAnswerAndData(true);
      this.rightAnswer(1300);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  playAnswer() {
    //      var notes = this.questions[this.questionNumber].question.keySig.getScale();
    const key = this.currentQuestion.question.parallelOrRelative.keySig;

    this.keySigPlayer.playAnswer(key);
  }

  getAnswerAndData(correct) {
    const correctAnswer = this.questions[this.questionNumber].answer;
    const { question } = this.questions[this.questionNumber];
    const answerQuality = question.keyQuality[0] === 'M' ? 'minor' : 'Major';
    const studentAnswerText = this.answer.letter + this.answer.accidental || '__';

    const answerAndData = {
      question: this.instructionsDiv.innerHTML,
      questionType: 'RelativeAndParallelKeys',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: `${studentAnswerText} ${answerQuality}`,
      studentAnswerSVG: null,
      correctAnswerText: `${correctAnswer} ${answerQuality}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.questions[this.questionNumber].skills,
    };

    return answerAndData;
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    this.displayDiv.removeChild(this.answerDiv);
    super.gameOver();
  }
}

RelativeAndParallelKeys.registry = {
  description: 'Relative and Parallel Keys',
  skills: ['relative_keys', 'parallel_keys'],
};

export default RelativeAndParallelKeys;
